import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator, Validators
} from "@angular/forms";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnInit
} from "@angular/core";
// noinspection TypeScriptPreferShortImport
import {getMaxTimeNativeValidator, getMinTimeNativeValidator, NTimePickerValue} from "../n-time-picker/n-time-picker.component";
import {distinctUntilChanged} from "rxjs/operators";
import {NInputErrorMessages} from "../public_components";
import {isSameDay} from "../../helpers";

@Component({
  selector: "n-date-time-picker",
  templateUrl: "./n-date-time-picker.html",
  styleUrls: ["./n-date-time-picker.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NDateTimePickerComponent),
      multi: true
    }, {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NDateTimePickerComponent),
      multi: true
    }
  ],
})
export class NDateTimePickerComponent implements ControlValueAccessor, Validator, OnInit {

  _hideTime: boolean;
  get hideTime() { return this._hideTime; }
  @Input() set displayTime(hideTime: any) {
    this._hideTime = !!hideTime;
    this.setTimeEnabling(!!hideTime);
    this.propagateValidation();
  }

  @Input() startDate: Date; // deprecated
  /**
   * Custom error message for input
   */
  @Input() errorMessages: NInputErrorMessages;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  minTime: NTimePickerValue;
  maxTime: NTimePickerValue;

  pickerDate: Date;
  // fields
  pickerTime: NTimePickerValue;
  viewDate: Date;
  hours: number;
  minuted: number;
  calendarControl = new FormControl();
  hourControl = new FormControl();
  private propagateChange: (_) => void;
  private propagateTouched: () => void;
  private propagateValidation: () => void;

  constructor(private cd: ChangeDetectorRef) {
    this.propagateValidation = () => {};
  }

  @Input()
  set disabled(val: boolean) { // deprecated
    this.setDisabledState(val);
  }

  @Input()
  set isDisabled(val: boolean) {
    this.setDisabledState(val);
  }

  @Input() set disableTime(val) {
    this.setTimeEnabling(!!val);
  }

  setTimeEnabling(enable: boolean) {
    if (enable) {
      this.hourControl.disable();
    } else {
      this.hourControl.enable();
    }
  }

  ngOnInit() {
    this.minuted = 0;
    this.hours = 0;

    this.calendarControl.valueChanges.subscribe((val) => {
      this.pickerDateChanged(val);
    });

    this.hourControl.valueChanges
      .pipe(
        distinctUntilChanged((x, y) => {
          if (!x && !y) { return true; }
          if ((x && !y) || (!x && y)) { return false; }
          return x.hours === y.hours && x.minutes === y.minutes;
        })
      )
      .subscribe((val) => {
        this.timeChanged(val);
      });
  }

  onBlur() {
    if (this.propagateTouched) {
      this.propagateTouched();
    }
  }

  pickerDateChanged(date: Date) {
    this.pickerDate = date;

    if (!date) {
      this.setMinMaxTimeFromDate(null);
      this.propagateChange(undefined);
      return;
    }

    this.viewDate = new Date(this.pickerDate.getTime());

    if (!this.pickerTime) {
      this.pickerTime = NTimePickerValue.createFromDate(this.viewDate);
      this.hourControl.setValue(this.pickerTime, {emitEvent: false});
    }

    this.setMinMaxTimeFromDate(date);
    this.setTimeFromPicker();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setTimeFromPicker() {
    if (this.pickerTime) {
      this.viewDate.setHours(this.pickerTime.hours);
      this.viewDate.setMinutes(this.pickerTime.minutes);
    }
    if (this.propagateChange) {
      this.propagateChange(cloneDate(this.viewDate));
    }
  }

  timeChanged(time: NTimePickerValue) {
    this.pickerTime = time;
    if (!this.pickerDate || !this.viewDate) {
      return;
    }
    this.setTimeFromPicker();
  }

  writeValue(obj: any): void {
    this.pickerDate = obj;
    if (this.pickerDate) {
      this.setMinMaxTimeFromDate(this.pickerDate);
      this.viewDate = new Date(this.pickerDate.getTime());
      this.pickerTime = NTimePickerValue.createFromDate(this.viewDate);
      this.calendarControl.setValue(new Date(this.pickerDate.getTime()), {emitEvent: false});
      this.hourControl.setValue(NTimePickerValue.createFromDate(this.viewDate), {emitEvent: false});
    } else {
      this.setMinMaxTimeFromDate(null);
      this.viewDate = undefined;
      this.pickerTime = undefined;
      this.calendarControl.setValue(null, {emitEvent: false});
      this.hourControl.setValue(null, {emitEvent: false});
    }
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.calendarControl.disable();
      this.hourControl.disable();
    } else {
      this.calendarControl.enable();
      this.hourControl.enable();
    }
    this.cd.markForCheck();
  }

  private setMinMaxTimeFromDate(date: Date | null) {
    this.minTime = isSameDay(this.minDate, date) ? NTimePickerValue.createFromDate(this.minDate) : null;
    this.maxTime = isSameDay(this.maxDate, date) ? NTimePickerValue.createFromDate(this.maxDate) : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.propagateValidation = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const req = Validators.required(this.calendarControl) || {};

    let minTime = {};
    let maxTime = {};
    if (this.hourControl.enabled) {
      if (this.minTime) { minTime = getMinTimeNativeValidator(this.minTime)(this.hourControl) || {}; }
      if (this.maxTime) { maxTime = getMaxTimeNativeValidator(this.maxTime)(this.hourControl) || {}; }
    }

    return {...req, ...minTime, ...maxTime};
  }
}

function cloneDate(d: Date): Date {
  return new Date(d.getTime());
}
